import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export type AutosaveResult = { ok: true } | { ok: false; message?: string };

export interface AutosaveState {
    status: AutosaveStatus;
    savedAt?: Date;
    errorMessage?: string;
}

interface Options<T> {
    save: (value: T) => Promise<AutosaveResult>;
    delayMs?: number;
}

/**
 * Debounced, serialized autosave. Values scheduled while a save is running replace each other and
 * only the latest goes out once it finishes, so an older response never lands after a newer one.
 *
 * @param options the save function and debounce delay
 * @returns the save state and the functions to schedule, flush, retry and mark a save
 */
export const useAutosave = <T>({ save, delayMs = 800 }: Options<T>) => {
    const [state, setState] = useState<AutosaveState>({ status: 'idle' });

    const saveRef = useRef(save);
    saveRef.current = save;

    const pendingRef = useRef<{ value: T } | null>(null);
    const failedRef = useRef<{ value: T } | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inFlightRef = useRef<Promise<void> | null>(null);
    const mountedRef = useRef(true);

    const updateState = useCallback((next: AutosaveState) => {
        if (mountedRef.current) {
            setState(next);
        }
    }, []);

    const drain = useCallback(async () => {
        while (pendingRef.current) {
            const { value } = pendingRef.current;
            pendingRef.current = null;
            updateState({ status: 'saving' });

            let result: AutosaveResult;
            try {
                result = await saveRef.current(value);
            } catch {
                result = { ok: false };
            }

            if (result.ok) {
                failedRef.current = null;
                // A newer value is already waiting, so stay in "saving" until it finishes
                if (!pendingRef.current) {
                    updateState({ status: 'saved', savedAt: new Date() });
                }
            } else {
                failedRef.current = { value };
                updateState({ status: 'error', errorMessage: result.message });
            }
        }
    }, [updateState]);

    const flush = useCallback((): Promise<void> => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (!inFlightRef.current) {
            inFlightRef.current = drain().finally(() => {
                inFlightRef.current = null;
                // Something was scheduled between the last loop check and this cleanup
                if (pendingRef.current) {
                    void flush();
                }
            });
        }
        return inFlightRef.current;
    }, [drain]);

    const schedule = useCallback(
        (value: T, { immediate = false }: { immediate?: boolean } = {}) => {
            pendingRef.current = { value };
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            if (immediate) {
                void flush();
            } else {
                timerRef.current = setTimeout(() => void flush(), delayMs);
            }
        },
        [delayMs, flush]
    );

    const retry = useCallback(() => {
        if (failedRef.current && !pendingRef.current) {
            schedule(failedRef.current.value, { immediate: true });
        }
    }, [schedule]);

    // For saves made outside this hook, e.g. an association saved by its own endpoint
    const markSaved = useCallback(() => {
        if (!pendingRef.current && !inFlightRef.current) {
            updateState({ status: 'saved', savedAt: new Date() });
        }
    }, [updateState]);

    const hasUnsavedChanges = useCallback(() => Boolean(pendingRef.current || inFlightRef.current), []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (pendingRef.current) {
                void flush();
            }
        };
    }, [flush]);

    return { ...state, schedule, flush, retry, markSaved, hasUnsavedChanges };
};
